import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAction,
  CharacterBook,
  SpellLimitation,
  CreatureLimitation,
  ItemLimitation,
  Campaign,
} from "@dndModels";
import { 
  BOOK_TYPES,
} from "@dndModels/Constants";
import { BookType } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import SpellLimitInput from "@dndComponents/limitations/SpellLimitInput";
import CreatureLimitInput from "@dndComponents/limitations/CreatureLimitInput";
import ItemLimitInput from "@dndComponents/limitations/ItemLimitInput";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import StringBox from "@components/input/strings/StringBox";
import CharacterViewSpellBookControls from "@dndComponents/character/inventory/book/CharacterViewSpellBookControls";

interface Props {
  char: Character | Campaign;
  char_book: CharacterBook;
  action?: CharacterAction;
  shared: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

/**
 * Making it so that Books can have data stored in them.  
 * Book Types: Spell, Creature, Item, Other.
 * Spell Books work kind of like a collection of Scrolls, but can only be used for learning, not casting, but don't get used up in the process.
 * Creature Books and Item Books are just used to record and read info about them.
 * Other is the standard one, and it just has a place for the owner to record whatever they want.
 */
export default function CharacterItemBookControls({
  char,
  char_book,
  // onChange,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  // const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  // const [roll, setRoll] = useState<RolledValue[]>([]);
  const [change_time, setChangeTime] = useState(0);
  const [view, setView] = useState("View");

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const enabled = api.user && (char.owner_id === api.user._id || (char instanceof Character && char.campaign && char.campaign.owner_id === api.user._id));

  const renderView = () => {
    if (view === "View") {
      return (
        <>
          { char_book.text.length > 0 &&
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements cfs={char instanceof Character ? char : undefined} display_string={char_book.text} />
            </Grid>
          }
          { renderViewView() }
        </>
      );
    } else {
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Text"
              value={char_book.text} 
              onBlur={(changed: string) => {
                char_book.text = changed;
                char.changed();
                setPing(!ping);
              }}
            />
          </Grid>
          { renderEdit() }
        </>
      );
    }
  }

  const renderViewView = () => {
    if (char_book.book_type === "Spell") {
      return renderViewSpellBook();
    } else if (char_book.book_type === "Creature") {
      return renderViewCreatureBook();
    } else if (char_book.book_type === "Item") {
      return renderViewItemBook();
    }
    return null;
  }

  const renderViewSpellBook = () => {
    if (char_book.limit instanceof SpellLimitation) {
      return (
        <CharacterViewSpellBookControls 
          char={char} 
          limit={char_book.limit} 
          onChange={() => {
            
          }}
        />
      );
    }
    return null;
  }

  const renderViewCreatureBook = () => {
    if (char_book.limit instanceof CreatureLimitation) {
      // return (
      //   <CreatureLimitInput
      //     obj={char_book.limit}
      //     change_time={change_time}
      //     onChange={(changed: CreatureLimitation) => {
      //       char_book.limit.copy(changed);
      //       setChangeTime(common_util.get_time_number());
      //     }}
      //   />
      // );
    }
    return null;
  }

  const renderViewItemBook = () => {
    if (char_book.limit instanceof ItemLimitation) {
      // return (
      //   <ItemLimitInput
      //     obj={char_book.limit}
      //     change_time={change_time}
      //     onChange={(changed: ItemLimitation) => {
      //       char_book.limit.copy(changed);
      //       setChangeTime(common_util.get_time_number());
      //     }}
      //   />
      // );
    }
    return null;
  }

  const renderEdit = () => {
    return (
      <>
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name="Book Type"
            options={BOOK_TYPES}
            value={char_book.book_type}
            onChange={(changed: BookType) => {
              char_book.book_type = changed;
              switch (changed) {
                case "Spell": char_book.limit = new SpellLimitation(); break;
                case "Creature": char_book.limit = new CreatureLimitation(); break;
                case "Item": char_book.limit = new ItemLimitation(); break;
              }
              char.changed();
              setReloading(true);
            }}
          />
        </Grid>
        { !reloading &&
          <Grid
            size={{
              xs: 12
            }}>
            { renderEditBookOfType() }
          </Grid>
        }
      </>
    );
  }

  const renderEditBookOfType = () => {
    if (char_book.book_type === "Spell") {
      return renderEditSpellBook();
    } else if (char_book.book_type === "Creature") {
      return renderEditCreatureBook();
    } else if (char_book.book_type === "Item") {
      return renderEditItemBook();
    }
    return null;
  }

  const renderEditSpellBook = () => {
    if (char_book.limit instanceof SpellLimitation) {
      return (
        <SpellLimitInput
          obj={char_book.limit}
          change_time={change_time}
          onChange={(changed: SpellLimitation) => {
            // console.log(changed);
            char_book.limit.copy(changed);
            char.changed();
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
    return null;
  }

  const renderEditCreatureBook = () => {
    if (char_book.limit instanceof CreatureLimitation) {
      return (
        <CreatureLimitInput
          obj={char_book.limit}
          change_time={change_time}
          onChange={(changed: CreatureLimitation) => {
            char_book.limit.copy(changed);
            char.changed();
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
    return null;
  }

  const renderEditItemBook = () => {
    if (char_book.limit instanceof ItemLimitation) {
      return (
        <ItemLimitInput
          obj={char_book.limit}
          change_time={change_time}
          onChange={(changed: ItemLimitation) => {
            char_book.limit.copy(changed);
            char.changed();
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
    return null;
  }

  return (
    <Grid container spacing={0} direction="row">
      { enabled &&
        <Grid
          size={{
            xs: 12
          }}>
          <CenteredMenu
            options={["View","Edit"]}
            selected={view}
            onSelect={setView}
          />
        </Grid>
      }
      { renderView() }
    </Grid>
  );
}
